import { Checkbox, Tooltip, type TooltipProps } from '@any-ui-react/core';
import { TdBody, TrBody } from '@any-ui-react/table';
import classNames from 'classnames';
import { type CSSProperties, Fragment, type PropsWithChildren, type ReactElement, type ReactNode } from 'react';

import { DEFAULT_COLUMN_MAX_WIDTH, EMPTY_DEFAULT } from '../constants';
import { type TableSelectionContextType, useTableSelectionCtx } from '../selection';
import type { TableLayout } from '../Table';
import { TableCollapseRow } from './TableCollapseRow';

export interface TableDesktopProps<T> {
  allowFloatingHeader?: boolean;
  borderBottomLastRow?: boolean;
  data: ReadonlyArray<T>;
  expandedRows?: Set<string>;
  fullWidth?: boolean;
  headerClassName?: string;
  hoverable?: boolean;
  layout: ReadonlyArray<TableLayout<T>>;
  loading?: boolean;
  numOfStickyCols?: number;
  renderDetailPanel?: (row: T, index: number) => ReactNode;
  rowClassName?: string;
  scrollContainerClassName?: string;
  trClassName?: string | ((row: T, index: number) => string);
  tableLayout?: CSSProperties['tableLayout'];
  onClick?: (row: T) => void;
  onToggleExpand?: (rowId: string) => void;
}

export function TableDesktop<T extends Record<string, unknown>>({
  borderBottomLastRow = false,
  data,
  expandedRows = new Set(),
  hoverable = true,
  layout,
  numOfStickyCols = 1,
  renderDetailPanel,
  rowClassName,
  trClassName,
  onClick,
  onToggleExpand,
}: PropsWithChildren<TableDesktopProps<T>>): ReactElement {
  const { check, uncheck, selection, selectable, isChecked } = useTableSelectionCtx<T>();

  return (
    <>
      {data.map((row, index) => {
        const isSelected = isChecked(row);
        const rowId = `row-${index}`;
        const isExpanded = expandedRows.has(rowId);

        const toggleDetailPanel = () => {
          onToggleExpand?.(rowId);
        };

        const resolvedTrClassName = typeof trClassName === 'function' ? trClassName(row, index) : trClassName;

        return (
          <Fragment key={index}>
            <TrBody
              className={classNames(
                'h-10 [&_td]:py-1',
                {
                  'group hover:bg-gray-0': hoverable,
                  'hover:bg-white': !hoverable,
                  'bg-gray-0': isSelected,
                  '[&:not(:last-of-type)]:border-b-0': !!renderDetailPanel,
                },
                resolvedTrClassName,
              )}
              onClick={() => onClick?.(row)}
            >
              {layout.map(
                (
                  {
                    cellRender,
                    checkboxTooltip,
                    accessor,
                    minWidth,
                    maxWidth,
                    width,
                    tdClassName,
                    truncated = true,
                    truncatedWithoutEllipsis,
                  },
                  i,
                ) => {
                  const resolvedTdClassName = typeof tdClassName === 'function' ? tdClassName(row) : tdClassName;
                  const hasCheckbox = i === 0 && selectable?.byField && selection;
                  const disabled = typeof selectable?.rowDisabled === 'function' && selectable.rowDisabled(row);
                  const cellNode = cellRender(row, index, {
                    expanded: isExpanded,
                    toggleDetailPanel,
                    isExpanded,
                    onToggleExpand: toggleDetailPanel,
                  });

                  return (
                    <TdBody
                      key={accessor}
                      className={classNames(
                        'border-gray-1 bg-inherit',
                        {
                          'border-t': index > 0,
                          'border-b': borderBottomLastRow && index === data.length - 1,
                        },
                        rowClassName,
                        resolvedTdClassName,
                        { truncate: truncated },
                        { '!text-clip': truncated && truncatedWithoutEllipsis },
                      )}
                      index={i}
                      style={{
                        minWidth: width ? '' : minWidth || 'none',
                        maxWidth: width ? '' : maxWidth || `${DEFAULT_COLUMN_MAX_WIDTH}px`,
                        width: width || 'auto',
                        zIndex: i < numOfStickyCols ? 10 : 0,
                      }}
                      title={typeof cellNode === 'string' || typeof cellNode === 'number' ? `${cellNode}` : undefined}
                    >
                      {hasCheckbox ? (
                        <CellWithCheckbox
                          accessor={accessor}
                          check={check}
                          checkboxTooltip={checkboxTooltip}
                          disabled={disabled}
                          isSelected={isSelected}
                          row={row}
                          uncheck={uncheck}
                        >
                          {cellNode || EMPTY_DEFAULT}
                        </CellWithCheckbox>
                      ) : (
                        cellNode || EMPTY_DEFAULT
                      )}
                    </TdBody>
                  );
                },
              )}
            </TrBody>
            {renderDetailPanel && (
              <TableCollapseRow colSpan={layout.length} isOpen={isExpanded}>
                {renderDetailPanel(row, index)}
              </TableCollapseRow>
            )}
          </Fragment>
        );
      })}
    </>
  );
}

interface CellWithCheckboxProps<T> {
  accessor: string;
  check: TableSelectionContextType<T>['check'];
  checkboxTooltip: ((row: T) => TooltipProps) | undefined;
  children: ReactNode;
  disabled: boolean;
  isSelected: boolean;
  row: T;
  uncheck: TableSelectionContextType<T>['uncheck'];
}

const CellWithCheckbox = <T,>({
  accessor,
  check,
  checkboxTooltip,
  children,
  disabled,
  isSelected,
  row,
  uncheck,
}: CellWithCheckboxProps<T>) => {
  const { label, ...restTooltipProps } = checkboxTooltip?.(row) || {};

  return (
    <div className="flex items-center">
      <div
        className="-ml-4 px-4"
        onClick={e => {
          if (disabled) {
            return;
          }

          e.stopPropagation();
        }}
      >
        <Tooltip key={accessor} disabled={!disabled} label={label} multiline={true} {...restTooltipProps}>
          <span key={accessor}>
            <Checkbox
              checked={isSelected}
              disabled={disabled}
              onChange={e => (e.currentTarget.checked ? check(row) : uncheck(row))}
            />
          </span>
        </Tooltip>
      </div>
      {children}
    </div>
  );
};
